import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Avatar,
    Grid,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    useMediaQuery
} from '@mui/material';
import { db } from '../firebase.js';
import { fetchSeasons } from '../services/seasons';
import { useAuction } from '../context/AuctionContext';
import { useNotification } from '../context/NotificationContext';
import { isScoutingLocked, scoutingLimit } from '../constants/game';
import { collection, getDocs, query, orderBy, doc, setDoc, arrayUnion, updateDoc, arrayRemove, getDoc } from 'firebase/firestore';
import { useTheme } from '@mui/material/styles';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { page } from '../theme/commonStyles';

// Kolommen voor de tabel per seizoen. `full` is de volledige titel (desktop en de
// tooltip op mobiel), `short` de compacte titel die op smalle schermen wordt getoond.
const SEASON_TABLE_COLUMNS = [
    { key: 'jaar', full: 'Jaar', short: 'Jaar', align: 'left' },
    { key: 'gespeeld', full: 'Gespeeld', short: 'Gs', align: 'right' },
    { key: 'doelpunten', full: 'Doelpunten', short: 'D', align: 'right' },
    { key: 'assists', full: 'Assists', short: 'A', align: 'right' },
    { key: 'punten', full: 'Punten', short: 'Pnt', align: 'right' },
];

const POSITION_MAP = {
    'Goalkeeper': 'Keeper',
    'Defender': 'Verdediger',
    'Midfielder': 'Middenvelder',
    'Attacker': 'Aanvaller',
    'Coach': 'Coach'
};

function PlayerDetailModal({ open, onClose, player, auctionId, seasonId, userId, showSeasonHistory = false }) {
    const theme = useTheme();
    const auctionCtx = useAuction();
    const notify = useNotification();
    const spelfase = auctionCtx?.auction?.spelfase;
    const [loading, setLoading] = useState(true);
    const [ownerName, setOwnerName] = useState(null);
    const [scoutingList, setScoutingList] = useState([]);
    const [scoutLoading, setScoutLoading] = useState(false);
    const [seasonStats, setSeasonStats] = useState([]);
    const [seasonStatsLoading, setSeasonStatsLoading] = useState(false);
    const [openStatTooltip, setOpenStatTooltip] = useState(null);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchPlayerOwnership = async () => {
            if (!open || !player || !auctionId) {
                return;
            }

            setLoading(true);
            setOwnerName(null);

            try {
                // Fetch all transfers to determine current owner
                const transfersQuery = query(
                    collection(db, 'auctions', auctionId, 'auctionTransfers'),
                    orderBy('timestamp', 'asc')
                );
                const transfersSnap = await getDocs(transfersQuery);

                // Use playerId if available, otherwise use id
                const playerIdToMatch = player.playerId || player.id;

                let currentOwner = null;
                transfersSnap.docs.forEach(doc => {
                    const transfer = doc.data();
                    // Compare both as numbers and as strings to be safe
                    if (transfer.playerId === playerIdToMatch || 
                        String(transfer.playerId) === String(playerIdToMatch) ||
                        Number(transfer.playerId) === Number(playerIdToMatch)) {
                        currentOwner = transfer.to;
                    }
                });

                // If player is owned by a participant, fetch their name
                if (currentOwner && currentOwner !== 'freemarket') {
                    const participantDocRef = doc(db, 'auctions', auctionId, 'participants', currentOwner);
                    const participantSnap = await getDoc(participantDocRef);
                    if (participantSnap.exists()) {
                        const participantName = participantSnap.data().name;
                        setOwnerName(participantName);
                    }
                }

            } catch (err) {
                console.error("Error fetching player ownership:", err);
                notify.error('Kon eigenaarsinformatie niet ophalen.');
            } finally {
                setLoading(false);
            }
        };

        fetchPlayerOwnership();
    }, [open, player, auctionId, notify]);

    // Fetch scouting list
    useEffect(() => {
        const fetchScoutingList = async () => {
            if (!open || !userId || !auctionId) return;

            try {
                const scoutingDocRef = doc(db, 'auctions', auctionId, 'auctionScoutinglist', userId);
                const scoutingSnap = await getDoc(scoutingDocRef);
                
                if (scoutingSnap.exists()) {
                    setScoutingList(scoutingSnap.data().scoutedPlayers || []);
                } else {
                    setScoutingList([]);
                }
            } catch (err) {
                console.error("Error fetching scouting list:", err);
                // Set empty list on error to prevent UI issues
                setScoutingList([]);
            }
        };

        fetchScoutingList();
    }, [open, userId, auctionId]);

    // Statistieken per seizoen ophalen uit playerScores/{seasonId}/totals. We tonen
    // het huidige seizoen en elk afgerond seizoen, nieuwste eerst, maar alleen als er
    // voor dat seizoen totalen zijn weggeschreven (via "Seizoenstotalen opslaan").
    useEffect(() => {
        const fetchSeasonStats = async () => {
            const playerId = player?.id || player?.playerId;
            if (!open || !showSeasonHistory || !playerId) {
                setSeasonStats([]);
                return;
            }

            setSeasonStatsLoading(true);
            try {
                const seasons = await fetchSeasons(); // nieuwste eerst

                // Toon elk seizoen waarvoor totalen zijn opgeslagen (huidig + elk eerder
                // seizoen). We filteren bewust op het bestaan van een totals-document in
                // plaats van op de seizoensstatus, zodat de historie ook klopt als een
                // status nog niet is bijgewerkt (bijv. een vorig seizoen dat nog 'actief' staat).
                const results = await Promise.all(seasons.map(async (season) => {
                    const totalsSnap = await getDoc(
                        doc(db, 'playerScores', String(season.id), 'totals', String(playerId)),
                    );
                    if (!totalsSnap.exists()) return null;
                    return {
                        key: String(season.id),
                        name: season.name || season.displayName || season.id,
                        isCurrent: String(season.id) === String(seasonId),
                        totals: totalsSnap.data(),
                    };
                }));

                setSeasonStats(results.filter(Boolean));
            } catch (err) {
                console.error('Kon seizoensstatistieken niet ophalen:', err);
                setSeasonStats([]);
            } finally {
                setSeasonStatsLoading(false);
            }
        };

        fetchSeasonStats();
    }, [open, showSeasonHistory, player, seasonId]);

    const handleToggleScout = async () => {
        if (!userId || !auctionId || !player) return;

        setScoutLoading(true);
        const docRef = doc(db, 'auctions', auctionId, 'auctionScoutinglist', userId);
        
        try {
            // Use id for scouting to match TransferMarket expectations
            // Convert to string to match TransferMarket format
            const scoutId = String(player.id || player.playerId);
            const isScouted = scoutingList.includes(scoutId);
            // Tijdens de Veiling ligt de scoutlijst vast: niets meer toevoegen of verwijderen.
            if (isScoutingLocked(spelfase)) {
                notify.warning('Tijdens de veiling kun je je scoutlijst niet meer wijzigen.');
                setScoutLoading(false);
                return;
            }
            // In de Voorbereiding mag een deelnemer maximaal MAX_SCOUTED_PLAYERS spelers scouten.
            const limit = scoutingLimit(spelfase);
            if (!isScouted && limit !== null && scoutingList.length >= limit) {
                notify.warning(`Je mag maximaal ${limit} spelers scouten. Verwijder eerst een gescoute speler.`);
                setScoutLoading(false);
                return;
            }
            if (isScouted) {
                await updateDoc(docRef, { scoutedPlayers: arrayRemove(scoutId) });
                setScoutingList(prev => prev.filter(id => id !== scoutId));
            } else {
                await setDoc(docRef, { scoutedPlayers: arrayUnion(scoutId) }, { merge: true });
                setScoutingList(prev => [...prev, scoutId]);
            }
        } catch (err) {
            console.error("Error updating scouting list:", err);
            notify.error("Fout bij het bijwerken van de scoutinglijst.");
        } finally {
            setScoutLoading(false);
        }
    };

    if (!player) return null;

    const dutchPosition = POSITION_MAP[player.position] || player.position;
    // Use id for scouting to match TransferMarket expectations
    // Convert to string to match TransferMarket format
    const scoutId = String(player.id || player.playerId);
    const isScouted = scoutingList.includes(scoutId);

    const StatRow = ({ label, value }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{value || 0}</Typography>
        </Box>
    );

    const statBoxSx = {
        p: 2,
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 1,
        border: '1px solid rgba(255, 255, 255, 0.1)'
    };

    // De totalen van het huidige seizoen (voor de bovenste sectie) en de rijen voor de
    // tabel per seizoen (huidig seizoen als bovenste rij, daarna nieuwste eerst).
    const currentSeasonStats = seasonStats.find((s) => s.isCurrent)?.totals || null;
    const seasonRows = [
        ...seasonStats.filter((s) => s.isCurrent),
        ...seasonStats.filter((s) => !s.isCurrent),
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            sx={{
                '& .MuiDialog-paper': {
                    width: { xs: 'calc(100% - 48px)' },
                    margin: { xs: '24px' }
                }
            }}
        >
            <DialogTitle>Spelerdetails</DialogTitle>
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                {loading && (<Box sx={page.loadingContainer}><CircularProgress /></Box>)}
                {!loading && (
                    <>
                        {/* Player Header */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            p: 2, 
                            bgcolor: theme.positions[dutchPosition]?.backgroundColor || 'rgba(169, 180, 200, 0.15)',
                            borderRadius: 1,
                            mb: 3
                        }}>
                            <Box sx={{ position: 'relative' }}>
                                <Avatar src={player.photoUrl} alt={player.name} sx={{ width: 60, height: 60 }}>
                                    <SportsSoccerIcon />
                                </Avatar>
                                {player.clubLogo && (
                                    <Box
                                        component="img"
                                        src={player.clubLogo}
                                        alt={`${player.club} logo`}
                                        sx={{
                                            position: 'absolute',
                                            bottom: -4,
                                            right: -6,
                                            height: 24,
                                            width: 24,
                                            objectFit: 'contain',
                                        }}
                                    />
                                )}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6">{player.name}</Typography>
                                    {player.position && (
                                        <Box sx={{
                                            ...theme.positions[dutchPosition],
                                            color: 'common.white',
                                            width: 24,
                                            height: 24,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 1,
                                            fontWeight: 'bold',
                                            fontSize: '0.875rem',
                                        }}>
                                            {dutchPosition.charAt(0)}
                                        </Box>
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">{player.club}</Typography>
                            </Box>
                        </Box>

                        {/* Ownership Info */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Eigenaar</Typography>
                            <Box sx={{ 
                                p: 1.5, 
                                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                                borderRadius: 1,
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                                {ownerName ? (
                                    <Typography variant="body2">{ownerName}</Typography>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">Vrije speler</Typography>
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Statistieken (Transfermarkt): huidig seizoen volledig + tabel per seizoen */}
                        {showSeasonHistory && (
                            <>
                                {seasonStatsLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <>
                                        {/* Sectie 1: alle statistieken van het huidige seizoen */}
                                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Statistieken huidig seizoen</Typography>
                                        {currentSeasonStats ? (
                                            <Box sx={statBoxSx}>
                                                <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, alignItems: 'stretch' }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <StatRow label="Totaal Punten" value={currentSeasonStats.totalPoints} />
                                                        <StatRow label="Wedstrijden" value={currentSeasonStats.matches ?? currentSeasonStats.played} />
                                                        <StatRow label="Basisplaatsen" value={currentSeasonStats.starting} />
                                                        <StatRow label="Minuten" value={currentSeasonStats.minutes} />
                                                        <StatRow label="Goals" value={currentSeasonStats.goals} />
                                                        <StatRow label="Assists" value={currentSeasonStats.assists} />
                                                    </Box>
                                                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <StatRow label="Reddingen" value={currentSeasonStats.saves} />
                                                        <StatRow label="Gewonnen kopduels" value={currentSeasonStats.aerielsWon} />
                                                        <StatRow label="Tackels" value={currentSeasonStats.tackles} />
                                                        <StatRow label="Intercepties" value={currentSeasonStats.interceptions} />
                                                        <StatRow label="Passes" value={currentSeasonStats.accuratePasses} />
                                                        <StatRow label="Geel" value={currentSeasonStats.yellow} />
                                                        <StatRow label="Rood" value={currentSeasonStats.red} />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Nog geen statistieken voor dit seizoen.
                                            </Typography>
                                        )}

                                        <Divider sx={{ my: 2 }} />

                                        {/* Sectie 2: tabel per seizoen (huidig seizoen bovenaan) */}
                                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Per seizoen</Typography>
                                        {seasonRows.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                Nog geen statistieken beschikbaar voor deze speler.
                                            </Typography>
                                        ) : (
                                            <TableContainer component={Box} sx={{ ...statBoxSx, p: 0, overflow: 'hidden' }}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            {SEASON_TABLE_COLUMNS.map((col) => (
                                                                <TableCell key={col.key} align={col.align}>
                                                                    {isMobile ? (
                                                                        <Tooltip
                                                                            title={col.full}
                                                                            arrow
                                                                            open={openStatTooltip === col.key}
                                                                            onClose={() => setOpenStatTooltip(null)}
                                                                            disableHoverListener
                                                                            disableFocusListener
                                                                            disableTouchListener
                                                                        >
                                                                            <Box
                                                                                component="span"
                                                                                onClick={() => setOpenStatTooltip((prev) => (prev === col.key ? null : col.key))}
                                                                                sx={{ cursor: 'pointer', userSelect: 'none' }}
                                                                            >
                                                                                {col.short}
                                                                            </Box>
                                                                        </Tooltip>
                                                                    ) : (
                                                                        col.full
                                                                    )}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {seasonRows.map(({ key, name, totals }) => (
                                                            <TableRow key={key}>
                                                                <TableCell sx={{ fontWeight: 'bold' }}>{name}</TableCell>
                                                                <TableCell align="right">{totals.matches ?? totals.played ?? 0}</TableCell>
                                                                <TableCell align="right">{totals.goals ?? 0}</TableCell>
                                                                <TableCell align="right">{totals.assists ?? 0}</TableCell>
                                                                <TableCell align="right">{totals.totalPoints ?? 0}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {/* Statistics (huidig seizoen, platte velden) — o.a. op de Spelersdata-pagina */}
                        {!showSeasonHistory && (
                            <>
                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Statistieken</Typography>
                                <Box sx={{
                                    p: 2,
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: 1,
                                    border: '1px solid rgba(255, 255, 255, 0.1)'
                                }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <StatRow label="Totaal Punten" value={player.totalPoints} />
                                            <StatRow label="Wedstrijden" value={player.played} />
                                            <StatRow label="Basisplaatsen" value={player.starting} />
                                            <StatRow label="Minuten" value={player.minutes} />
                                            <StatRow label="Goals" value={player.goals} />
                                            <StatRow label="Assists" value={player.assists} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <StatRow label="Reddingen" value={player.saves} />
                                            <StatRow label="Gewonnen kopduels" value={player.aerielsWon} />
                                            <StatRow label="Tackels" value={player.tackles} />
                                            <StatRow label="Intercepties" value={player.interceptions} />
                                            <StatRow label="Passes" value={player.accuratePasses} />
                                            <StatRow label="Geel" value={player.yellow} />
                                            <StatRow label="Rood" value={player.red} />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                {!ownerName && (
                    <Button
                        variant="outlined"
                        startIcon={scoutLoading ? <CircularProgress size={16} /> : (isScouted ? <StarIcon /> : <StarBorderIcon />)}
                        onClick={handleToggleScout}
                        disabled={scoutLoading || isScoutingLocked(spelfase)}
                        color={isScouted ? 'secondary' : 'inherit'}
                    >
                        {isScouted ? 'Gescouted' : 'Scouten'}
                    </Button>
                )}
                <Button onClick={onClose}>Sluiten</Button>
            </DialogActions>
        </Dialog>
    );
}

export default PlayerDetailModal;
